// RotationDialog.jsx
// This component provides a dialog for rotating and mirroring images before importing them.

// Copyright HS Analysis GmbH, 2025
// Author: Viktor Eberhardt

// Framework imports
import React, { useState } from "react";

// MUI imports
import { makeStyles } from "@mui/styles";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import PropTypes from "prop-types";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";

const useStyles = makeStyles(() => ({
  root: {
    marginBottom: 16,
  },
  previewContainer: {
    position: "relative",
    width: 400,
    height: 400,
    marginTop: 16,
    overflow: "hidden",
    background: "#fafafa",
    borderRadius: 8,
    border: "1px solid #eee",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  topContent: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  previewImage: {
    position: "absolute",
    top: "50%",
    left: "50%",
    maxWidth: "80%",
    maxHeight: "80%",
    display: "block",
  },
}));

function RotationDialog({ open, onClose, onConfirm, src }) {
  const classes = useStyles();
  const [angle, setAngle] = useState(0);
  const [mirrored, setMirrored] = useState(false);
  const [dragging, setDragging] = useState(false); // Track slider drag

  if (!src || !open) {
    return null;
  }

  /**
   * Handles the change event for the angle input field.
   * Updates the angle state with the numeric value from the input.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event from the angle input field.
   */
  const handleAngleChange = (e) => {
    setAngle(Number(e.target.value));
  };

  /**
   * Toggles the mirrored state of the component.
   * When invoked, it switches the current mirrored value between true and false.
   */
  const handleMirror = () => {
    setMirrored((prev) => !prev);
  };

  /**
   * Handles the import action by invoking the onConfirm callback with the current angle and mirrored state,
   * then closes the dialog.
   */
  const handleImport = () => {
    if (onConfirm) onConfirm({ angle, mirrored });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Rotate</DialogTitle>
      <DialogContent>
        <div className={classes.root}>
          <div className={classes.topContent}>
            <span style={{ minWidth: 40 }}>Angle</span>
            <Slider
              value={angle}
              onChange={(_, value) => setAngle(value)}
              min={-180}
              max={180}
              step={1}
              marks={[
                { value: -180, label: "-180°" },
                { value: 0, label: "0°" },
                { value: 180, label: "180°" },
              ]}
              valueLabelDisplay="auto"
              sx={{ width: 200 }}
              onChangeCommitted={() => setDragging(false)}
              onMouseDown={() => setDragging(true)}
              onTouchStart={() => setDragging(true)}
              onMouseUp={() => setDragging(false)}
              onTouchEnd={() => setDragging(false)}
            />
            <TextField
              label="Angle"
              type="number"
              value={angle}
              onChange={handleAngleChange}
              inputProps={{
                step: 1,
                min: -180,
                max: 180,
                style: { width: 60 },
              }}
              size="small"
            />
            <Button
              onClick={handleMirror}
              variant={mirrored ? "contained" : "outlined"}
            >
              {mirrored ? "Mirrored" : "Mirror"}
            </Button>
          </div>
          <div className={classes.previewContainer}>
            <img
              src={src}
              alt="Rotation Preview"
              className={classes.previewImage}
              style={{
                transform: `translate(-50%, -50%) rotate(${angle}deg) scaleX(${
                  mirrored ? -1 : 1
                })`,
                transition: dragging ? "none" : "transform 0.1s",
              }}
            />
          </div>

          <p>
            The selected image will be rotated. Then rotation angle will be
            applied to the original file. Possible previous rotations will be
            ignored.
          </p>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleImport} variant="contained" color="primary">
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RotationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  src: PropTypes.string,
};

export default RotationDialog;
